import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth, DEV_BYPASS_ENABLED } from "@/lib/auth-context";
import NoderaMark from "@/components/NoderaMark";
import { Building2, Lock } from "lucide-react";

export default function VendorLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const { setSession } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/auth/vendor/login", form);
      setSession(r.data.token, r.data.user);
      toast.success(`Welcome back, ${r.data.user.vendor_name || "vendor"}`);
      nav("/vendor");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const devBypass = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/auth/dev/vendor-bypass");
      setSession(r.data.token, r.data.user);
      toast.success("Dev vendor session granted.");
      setTimeout(() => nav("/vendor"), 50);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="vendor-login-page"
      className="min-h-screen bg-white relative overflow-hidden flex flex-col"
    >
      <span className="hero-blob bg-emerald-100 top-[-160px] left-[-160px] w-[420px] h-[420px]" />
      <span className="hero-blob bg-blue-100 bottom-[-160px] right-[-160px] w-[420px] h-[420px] opacity-60" />

      <header className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="vendor-login-brand"
          className="flex items-center gap-2.5"
        >
          <span className="inline-flex w-7 h-7 items-center justify-center text-[#1F4853]">
            <NoderaMark size={24} />
          </span>
          <span className="font-display font-bold text-slate-900 text-base tracking-tight">
            Nodera <span className="text-slate-400 font-medium">Health</span>
          </span>
        </Link>
        <div className="text-xs text-slate-400 font-medium uppercase tracking-[0.18em]">
          Vendor portal
        </div>
      </header>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 mx-auto flex items-center justify-center text-white shadow-sm">
              <Building2 className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-3 tracking-tight">
              Vendor portal
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Sign in to manage workflow requests and invoices.
            </p>
          </div>
          <form
            onSubmit={submit}
            data-testid="vendor-login-form"
            className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-sm"
          >
            <Field label="Email">
              <input
                type="email"
                required
                data-testid="vendor-login-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
                placeholder="vendor@yourcompany.com"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                required
                data-testid="vendor-login-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
              />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="vendor-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
            >
              <Lock className="w-3.5 h-3.5" /> {submitting ? "Signing in…" : "Sign in"}
            </button>
            <Link
              to="/auth/password/forgot"
              className="block text-center text-xs text-slate-500 hover:text-slate-700"
            >
              Forgot password?
            </Link>
            {DEV_BYPASS_ENABLED && (
              <button
                type="button"
                onClick={devBypass}
                data-testid="vendor-login-dev-bypass-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-dashed border-emerald-300 bg-emerald-50/40 text-emerald-700 hover:bg-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <Lock className="w-3 h-3" /> Dev · Skip auth (vendor sandbox)
              </button>
            )}
          </form>
          <div className="text-center mt-4 text-xs text-slate-400">
            Are you a clinic?{" "}
            <Link to="/login" className="font-semibold text-blue-700 hover:text-blue-900">
              Clinic sign in
            </Link>
          </div>
        </div>
      </div>

      <footer className="relative z-10 border-t border-slate-100 bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Nodera Health</div>
          <div className="font-medium">Operational infrastructure only · No clinical care</div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
